import React, { cloneElement, useState, useCallback, useRef, Fragment } from 'react'
import {
  offset,
  flip,
  shift,
  arrow,
  autoUpdate,
  useFloating,
  useInteractions,
  useRole,
  useDismiss,
  useId,
  useClick,
  FloatingPortal
} from '@floating-ui/react-dom-interactions'
import cx from 'classnames'

import styles from './ReactPopover.module.scss'

const getMiddlewareConfig = (config, arrowRef) => {
  if (!config || !config.length) {
    return [offset(10), flip(), shift(), arrow({ element: arrowRef })]
  }

  return [...config, arrow({ element: arrowRef })]
}

const Popover = (props) => {
  const { children, render, middleware, dismissConfig, className } = props
  const position = props.placement
  const [open, setOpen] = useState(false)

  const arrowRef = useRef(null)

  const options = {
    placement: position ?? 'right',
    middleware: getMiddlewareConfig(middleware, arrowRef)
  }
  const dismiss = dismissConfig ?? {
    escapeKey: false,
    outsidePointerDown: false
  }

  const { x, y, reference, floating, strategy, context, update, middlewareData, placement } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: options.middleware,
    placement: options.placement,
    whileElementsMounted: autoUpdate
  })
  const arrowPosition = middlewareData.arrow
  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right'
  }[placement.split('-')[0]]

  const id = useId()
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, {
      toggle: false
    }),
    useRole(context),
    useDismiss(context, { ...dismiss })
  ])

  const childrenEl = cloneElement(children, getReferenceProps({ ref: reference, ...children.props }))

  const arrowCallback = useCallback(
    (node) => {
      arrowRef.current = node
      update()
    },
    [arrowRef, update]
  )

  return (
    <Fragment>
      {childrenEl}
      {open && (
        <FloatingPortal id={id}>
          <div
            {...getFloatingProps({
              className: `${styles.Popover} ${className ?? ''}`,
              ref: floating,
              style: {
                position: strategy,
                top: y ?? 0,
                left: x ?? 0
              }
            })}
          >
            {render({
              close: () => {
                setOpen(false)
              },
              arrow: (
                <div
                  ref={arrowCallback}
                  style={{
                    left: arrowPosition?.x != null ? `${arrowPosition.x}px` : '',
                    top: arrowPosition?.y != null ? `${arrowPosition.y}px` : '',
                    right: '',
                    bottom: '',
                    [staticSide]: '-7px'
                  }}
                  className={cx(styles.Arrow, styles.Right)}
                />
              )
            })}
          </div>
        </FloatingPortal>
      )}
    </Fragment>
  )
}

export default Popover
